import React, { useEffect, useRef, useState } from 'react';

interface ParallaxImageProps {
  src: string;
  className?: string;
  style?: React.CSSProperties;
  overlay?: string;
  children?: React.ReactNode;
  ariaLabel?: string;
}

/** Full-bleed background image that scales gently from 1 to 1.08 as it passes through the viewport. */
const ParallaxImage: React.FC<ParallaxImageProps> = ({ src, className = '', style, overlay, children, ariaLabel }) => {
  const ref = useRef<HTMLElement>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const progress = Math.min(1, Math.max(0, (vh - rect.top) / (vh + rect.height)));
      setScale(1 + progress * 0.08);
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <section ref={ref} className={`relative overflow-hidden ${className}`} style={style} aria-label={ariaLabel}>
      <div
        className="absolute inset-0 bg-cover bg-center will-change-transform"
        style={{ backgroundImage: `url(${src})`, transform: `scale(${scale})` }}
      />
      {overlay && <div className="absolute inset-0" style={{ background: overlay }} />}
      {children && <div className="relative z-10 w-full">{children}</div>}
    </section>
  );
};

export default ParallaxImage;
